import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useEffectOnce, usePagination, usePathParam, useViewport } from '@chilecompra/react-kit'
import { Pagination, Skeleton, Typography } from '@chilecompra/react-kit/components'
import { PUBLIC_URL } from '../../config/settings/environment'
import { GENERAL_INFORMATION_TABS } from '../../config/settings/constant'
import { onGetDependentBuyerThunk } from './DependentBuyerSection.actions'
import {
  Card,
  CardItem,
  Column,
  QuestionIcon,
  Root,
  Row,
  Table,
  TableCell,
  TableRow,
  Tooltip,
  WarningImg
} from './DependentBuyerSection.styles'

/**
 * Dependent buyer section container.
 */
const DependentBuyerSection = () => {
  const {
    size: { isUpToMedium, isTiny }
  } = useViewport()
  const dispatch = useDispatch()
  const dni = usePathParam('rut')
  const { loading, error, dependentBuyer = [] } = useSelector(state => state.dependentBuyer)

  useEffectOnce(() => {
    dispatch(onGetDependentBuyerThunk({ dni }))
  }, [dni, dispatch])

  const rowsPerPage = 2
  const { page, totalPages, records, onChangePage, onGetCurrentPage } = usePagination({
    data: dependentBuyer,
    initialPage: 1,
    initialRowsPerPage: rowsPerPage
  })

  return (
    <Root>
      {!loading && error ? (
        <>
          <Typography
            color="black1"
            fontSize="16px"
            fontWeight="bold"
            variant="body1"
            lineHeight="21px"
            padding="15px 0 5px 0"
            tabIndex="0"
            sx={{ maxWidth: { lg: '603px' } }}
          >
            Organismos compradores dependientes
            <Tooltip
              content="Son unidades dentro de una institución pública que, debido a la naturaleza de sus fondos, pueden actuar como persona jurídica. Como por ejemplo, la dirección de bienestar de una institución pública, que puede recibir fondos por aportes del funcionariado, entre otros."
              placement={isUpToMedium ? 'right-end' : 'top-end'}
              tabIndex="0"
            >
              <QuestionIcon src={`${PUBLIC_URL}/assets/images/question-icon.svg`} alt="Ayuda" />
            </Tooltip>
          </Typography>
          <Typography
            color="black1"
            fontSize="14px"
            fontWeight="regular"
            variant="body2"
            lineHeight="18px"
            align="left"
            tabIndex="0"
            padding="5px 0 16px 0"
            sx={{ maxWidth: { sm: '458px' } }}
          >
            {loading && <Skeleton height="55px" variant="rectangular" width="50%" />}
            {!loading &&
              `Son aquellas unidades de compra internas de una institución que operan bajo un RUT distinto al del organismo del cual depende.`}
          </Typography>
          <Card
            display="flex"
            enableBorder
            color="blank"
            margin="43px 0 44px 0"
            alignItems="center"
            height={isTiny ? '139px' : '243px'}
          >
            <CardItem direction="row" padding="25px 16px">
              <WarningImg alt="No se encontraron resultados" src={`${PUBLIC_URL}/assets/images/warning.svg`} />
              <Typography
                color="black1"
                variant="body1"
                fontWeight="regular"
                fontSize="16px"
                lineHeight="21px"
                margin="0 0 0 33px"
                sx={{ maxWidth: '520px', 'white-space': 'pre-wrap' }}
              >
                {GENERAL_INFORMATION_TABS.GENERAL_INFORMATION.SERVICE_ERROR}
              </Typography>
            </CardItem>
          </Card>
        </>
      ) : (
        <Row>
          <Column xs={12} sm={12} md={6} lg={4}>
            {!loading && (
              <Typography
                color="black1"
                fontSize="16px"
                fontWeight="bold"
                variant="body1"
                lineHeight="21px"
                padding="15px 0 5px 0"
                tabIndex="0"
                sx={{ maxWidth: { lg: '258px' } }}
              >
                Organismos compradores dependientes
                <Tooltip
                  content="Son unidades dentro de una institución pública que, debido a la naturaleza de sus fondos, pueden actuar como persona jurídica. Como por ejemplo, la dirección de bienestar de una institución pública, que puede recibir fondos por aportes del funcionariado, entre otros."
                  placement={isUpToMedium ? 'right-end' : 'top-end'}
                  tabIndex="0"
                >
                  <QuestionIcon src={`${PUBLIC_URL}/assets/images/question-icon.svg`} alt="Ayuda" />
                </Tooltip>
              </Typography>
            )}
            {loading && <Skeleton height="35px" variant="rectangular" width="60%" />}
            <Typography
              color="black1"
              fontSize="14px"
              fontWeight="regular"
              variant="body2"
              lineHeight="18px"
              align="left"
              tabIndex="0"
              padding="5px 0 16px 0"
              sx={{ maxWidth: { sm: '458px' } }}
            >
              {loading && <Skeleton height="55px" variant="rectangular" width="50%" />}
              {!loading &&
                `Son aquellas unidades de compra internas de una institución que operan bajo un RUT distinto al del organismo del cual depende.`}
            </Typography>
          </Column>
          <Column xs={12} sm={12} md={6} lg={8}>
            <Table accessibility={{ label: 'Organismo compradores dependientes' }} padding="0 0 16px">
              {!loading && dependentBuyer.length === 0 && (
                <Typography
                  color="black1"
                  fontSize="14px"
                  fontWeight="regular"
                  variant="body2"
                  lineHeight="18px"
                  tabIndex="0"
                >
                  {GENERAL_INFORMATION_TABS.DEPENDENT_BUYER_STATUS.NO_DATA}
                </Typography>
              )}
              {!loading && dependentBuyer.length > 0 && (
                <>
                  <TableRow>
                    <TableCell enableHead tabIndex="0">
                      Razón Social
                    </TableCell>
                    <TableCell enableHead alignment="left" tabIndex="0">
                      RUT
                    </TableCell>
                  </TableRow>
                  {onGetCurrentPage().map((buyers, index) => {
                    return (
                      <TableRow key={index}>
                        <TableCell alignment="left" tabIndex="0">
                          {buyers.orgLegalName}
                        </TableCell>
                        <TableCell tabIndex="0">{buyers.dni}</TableCell>
                      </TableRow>
                    )
                  })}
                </>
              )}
              {loading && (
                <>
                  <Skeleton height="15px" variant="rectangular" width="100%" />
                  <Skeleton height="30px" variant="rectangular" width="100%" />
                  <Skeleton height="30px" variant="rectangular" width="100%" />
                </>
              )}
            </Table>
            {!loading && totalPages > 1 && (
              <Pagination
                alignment="center"
                size="small"
                total={dependentBuyer.length}
                records={records}
                onChange={onChangePage}
                count={totalPages}
                page={page}
                tabIndex="0"
              />
            )}
          </Column>
        </Row>
      )}
    </Root>
  )
}

export default DependentBuyerSection
